import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ExternalLink } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminRelease } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export default function DiscographyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [release, setRelease] = useState<AdminRelease | null>(
    (location.state as { release?: AdminRelease } | null)?.release ?? null
  )
  const [loading, setLoading] = useState(!release)

  useEffect(() => {
    if (release || !token || !id) return
    api.getAdminDiscography(token)
      .then((items) => setRelease(items.find((r) => r.id === id) ?? null))
      .finally(() => setLoading(false))
  }, [release, id, token])

  const handleDelete = async () => {
    if (!release) return
    if (!await confirm(
      `"${release.title}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${release.title}"` }
    )) return
    if (!token) return
    try {
      await api.deleteDiscography(token, release.id)
      navigate(-1)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!release) {
    return <div className="p-6"><p className="text-red-400 text-sm">Release not found.</p></div>
  }

  const totalTracks = release.tracks.length + (release.more_tracks ?? 0)

  return (
    <div className="p-6">
      {/* Nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/discography/${release.id}/edit`, { state: { release } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
          >
            Hapus
          </button>
        </div>
      </div>

      <div className="max-w-xl mx-auto">
      {/* Hero card */}
      <div className="flex bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden mb-4">
        {/* Square cover art */}
        <div className="w-36 flex-shrink-0 bg-zinc-800">
          {release.image_url ? (
            <img
              src={normalizeImageKitUrl(release.image_url)}
              alt={release.alt || release.title}
              className="w-full h-full object-cover aspect-square"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="w-36 h-36 flex items-center justify-center">
              <span className="text-zinc-600 text-4xl font-bold">{release.title[0]}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
          <div>
            {release.label && (
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                {release.label}
              </p>
            )}
            <h1 className="text-xl font-bold text-white leading-tight">{release.title}</h1>
            <button
              type="button"
              onClick={() => navigate(`/groups?slug=${release.group_slug}`)}
              className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors mt-1 block"
            >
              {release.group_slug}
            </button>
          </div>

          <div className="mt-4 space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-zinc-600 w-16 flex-shrink-0">Rilis</span>
              <span className="text-xs text-zinc-300">{release.release_date}</span>
            </div>
            {totalTracks > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-600 w-16 flex-shrink-0">Tracks</span>
                <span className="text-xs text-zinc-300">{totalTracks} lagu</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Track list */}
        {release.tracks.length > 0 && (
          <Section title="Track List">
            <ol className="space-y-1.5">
              {release.tracks.map((track, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="text-[10px] text-zinc-600 w-5 text-right flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-zinc-300">{track}</span>
                </li>
              ))}
            </ol>
            {release.more_tracks && release.more_tracks > 0 && (
              <p className="text-[11px] text-zinc-600 mt-3 pl-8">
                +{release.more_tracks} track lainnya
              </p>
            )}
          </Section>
        )}

        {/* Spotify */}
        {release.spotify_url && (
          <a
            href={release.spotify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-xl px-5 py-4 transition-colors group"
          >
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Dengarkan di</p>
              <p className="text-sm font-medium text-white group-hover:text-green-400 transition-colors">Spotify</p>
            </div>
            <ExternalLink size={14} className="text-zinc-600 group-hover:text-green-400 transition-colors flex-shrink-0" />
          </a>
        )}

        {/* Meta */}
        <Section title="Info Teknis">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Release ID</p>
              <p className="text-xs text-zinc-600 font-mono break-all">{release.id}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Group</p>
              <p className="text-xs text-zinc-400 font-mono">{release.group_slug}</p>
            </div>
          </div>
        </Section>
      </div>

      </div>
      {dialog}
    </div>
  )
}
